import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import theme from "../../styles/theme";

const Root = styled.div`
  position: relative;
  font-family: "Montserrat", sans-serif;
  user-select: none;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: ${({ $open }) => ($open ? theme.colors.dark : theme.colors.darkLight)};
  border: 2px solid ${({ $open }) => ($open ? theme.colors.grey : theme.colors.secondaryDark)};
  color: inherit;
  text-align: left;
  padding: 7px 12px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  min-width: ${({ $minWidth }) => $minWidth}px;
  max-width: ${({ $maxWidth }) => $maxWidth}px;

  &:hover {
    border-color: ${theme.colors.grey};
  }
`;

const TriggerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TriggerLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${theme.colors.grey};
  text-align: left;
`;

// All slots share the same grid cell so the stack's intrinsic width is always the widest
// option label, keeping the trigger's width stable as the selected value changes.
const TriggerValueStack = styled.span`
  display: inline-grid;
  min-width: 0;
`;

const TriggerValueSlot = styled.span`
  grid-area: 1 / 1;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TriggerValueGhost = styled(TriggerValueSlot)`
  visibility: hidden;
`;

const Caret = styled.span`
  flex: none;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid ${theme.colors.green};
  transition: transform 0.18s;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Panel = styled.div`
  position: absolute;
  z-index: 200;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-height: 300px;
  overflow: auto;
  background: ${theme.colors.dark};
  border: 2px solid ${theme.colors.secondaryDark};
  box-shadow: 0 18px 40px -12px #000000d9;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Option = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 12px;
  font-size: 13px;
  font-family: "Montserrat", sans-serif;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  color: ${({ $selected }) => ($selected ? theme.colors.green : theme.colors.white)};
  background: ${({ $selected }) => ($selected ? `${theme.colors.green}1a` : "transparent")};
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.12s;

  &:hover {
    background-color: ${theme.colors.secondaryDark};
  }
`;

export function Dropdown({ label, value, options, onChange, minWidth = 132, maxWidth = 240 }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onAway = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onAway);
    return () => document.removeEventListener("mousedown", onAway);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        $open={open}
        $minWidth={minWidth}
        $maxWidth={maxWidth}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <TriggerText>
          <TriggerLabel>{label}</TriggerLabel>
          <TriggerValueStack>
            {options.map((o) => (
              <TriggerValueGhost key={o.value ?? "null"} aria-hidden="true">
                {o.label}
              </TriggerValueGhost>
            ))}
            <TriggerValueSlot>{current ? current.label : "—"}</TriggerValueSlot>
          </TriggerValueStack>
        </TriggerText>
        <Caret $open={open} />
      </Trigger>
      {open && (
        <Panel role="listbox">
          {options.map((o) => (
            <Option
              key={o.value ?? "null"}
              type="button"
              role="option"
              aria-selected={o.value === value}
              $selected={o.value === value}
              onClick={() => {
                setOpen(false);
                onChange(o.value);
              }}
            >
              {o.label}
            </Option>
          ))}
        </Panel>
      )}
    </Root>
  );
}
